/**
 * Google Analytics 4 Provider
 *
 * Implements GA4 tracking with gtag.js.
 * Ported from deepsource.com composables/analytics/providers/googleAnalytics.ts
 */

import type { AnalyticsProvider } from '~/types/tracking'
import type { CookieConsentService } from '../CookieConsentService'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

const isDev = process.env.NODE_ENV === 'development'

interface GoogleAnalyticsConfig {
  measurementId: string
}

export const createGoogleAnalyticsProvider = (
  config: GoogleAnalyticsConfig,
  consentService: CookieConsentService
): AnalyticsProvider => {
  let isLoaded = false
  const { measurementId } = config

  const loadScript = (id: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
        resolve()
        return
      }

      const script = document.createElement('script')
      script.async = true
      script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Failed to load Google Analytics script'))
      document.head.appendChild(script)
    })
  }

  const initGtag = (): void => {
    window.dataLayer = window.dataLayer || []
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments)
    }
    window.gtag('js', new Date())
  }

  return {
    name: 'google-analytics',

    init() {
      if (!measurementId) {
        if (isDev) {
          console.log('Google Analytics: disabled (no measurement ID configured)')
        }
        return
      }

      initGtag()

      if (window.gtag) {
        window.gtag('consent', 'default', {
          analytics_storage: consentService.hasConsent('analytics') ? 'granted' : 'denied',
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied'
        })
      }

      loadScript(measurementId)
        .then(() => {
          if (window.gtag) {
            window.gtag('config', measurementId, {
              send_page_view: false
            })
          }
          isLoaded = true

          if (isDev) {
            console.log('Google Analytics initialized:', measurementId)
          }
        })
        .catch((error) => {
          if (isDev) {
            console.warn('Failed to initialize Google Analytics:', error)
          }
        })
    },

    identify(userId, properties) {
      if (!window.gtag) return

      window.gtag('config', measurementId, {
        user_id: userId,
        send_page_view: false
      })

      if (properties) {
        window.gtag('set', 'user_properties', properties)
      }

      if (isDev) {
        console.log('Google Analytics user identified:', userId)
      }
    },

    reset() {
      if (!window.gtag) return

      window.gtag('config', measurementId, {
        user_id: undefined,
        send_page_view: false
      })

      if (isDev) {
        console.log('Google Analytics user reset')
      }
    },

    track(event, properties) {
      if (!window.gtag) return

      const eventMapping: Record<string, string> = {
        'Sign Up': 'sign_up',
        SignUp: 'sign_up',
        'User Signed Up': 'sign_up',
        trial_signup_intent: 'sign_up',
        Login: 'login',
        'User Logged In': 'login',
        'Page View': 'page_view',
        Purchase: 'purchase',
        'Button Clicked': 'select_content',
        'Link Clicked': 'select_content'
      }

      const gaEvent = eventMapping[event] || event

      window.gtag('event', gaEvent, properties)

      if (isDev) {
        console.log('Google Analytics event tracked:', gaEvent, properties)
      }
    },

    setUserProperties(properties) {
      if (!window.gtag) return

      window.gtag('set', 'user_properties', properties)

      if (isDev) {
        console.log('Google Analytics user properties set:', properties)
      }
    },

    isAvailable() {
      return !!measurementId && isLoaded
    }
  }
}
